import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const StayIntroPage: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'zh-TW' ? zhTW : enUS;

  const accommodationTypes = [
    {
      icon: '🏨',
      title: t.types.hotel.title,
      description: t.types.hotel.description,
      features: t.types.hotel.features,
      price: t.types.hotel.price,
      bestFor: t.types.hotel.bestFor
    },
    {
      icon: '🏠',
      title: t.types.homestay.title,
      description: t.types.homestay.description,
      features: t.types.homestay.features,
      price: t.types.homestay.price,
      bestFor: t.types.homestay.bestFor
    },
    {
      icon: '🛏️',
      title: t.types.hostel.title,
      description: t.types.hostel.description,
      features: t.types.hostel.features,
      price: t.types.hostel.price,
      bestFor: t.types.hostel.bestFor
    },
    {
      icon: '🏡',
      title: t.types.villa.title,
      description: t.types.villa.description,
      features: t.types.villa.features,
      price: t.types.villa.price,
      bestFor: t.types.villa.bestFor
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* 頁面標題 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t.subtitle}
          </p>
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            🏠 {t.cta}
          </Link>
        </div>

        {/* 住宿特色 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="text-4xl mb-4">🌊</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.view.title}</h3>
            <p className="text-gray-600">{t.features.view.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">💻</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.nomad.title}</h3>
            <p className="text-gray-600">{t.features.nomad.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.price.title}</h3>
            <p className="text-gray-600">{t.features.price.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🤝</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.community.title}</h3>
            <p className="text-gray-600">{t.features.community.description}</p>
          </div>
        </div>

        {/* 住宿類型 */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t.types.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {accommodationTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-soft p-8 hover:shadow-medium transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-5xl mb-4">{type.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 mb-6">{type.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">💰</span>
                    {type.price}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">👥</span>
                    {type.bestFor}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {type.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 推薦區域 */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
            {t.areas.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.areas.list.map((area, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{area.icon}</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {area.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {area.description}
                </p>
                <div className="text-sm text-blue-600 font-medium">
                  {area.highlight}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 預約流程 */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
            {t.process.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {t.process.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 行動按鈕 */}
        <div className="text-center">
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            🎯 {t.startBooking}
          </Link>
        </div>
      </div>
    </div>
  );
};

// 中文翻譯
const zhTW = {
  title: '住宿推薦',
  subtitle: '從海景民宿到市區飯店，從經濟青旅到豪華別墅，我們為您精選花蓮最適合數位游牧者的住宿選項',
  cta: '開始預約住宿',
  startBooking: '立即開始預約',
  features: {
    view: {
      title: '絕佳景觀',
      description: '海景、山景、市區景觀，滿足不同需求'
    },
    nomad: {
      title: '數位游牧友善',
      description: '高速WiFi、工作空間、長期住宿優惠'
    },
    price: {
      title: '價格實惠',
      description: '從經濟型到豪華型，滿足各種預算'
    },
    community: {
      title: '社群氛圍',
      description: '與其他數位游牧者交流，建立人脈'
    }
  },
  types: {
    title: '住宿類型',
    hotel: {
      title: '飯店',
      description: '專業服務，設施完善，適合商務和休閒',
      features: ['24小時櫃台', '健身房', '餐廳'],
      price: 'NT$ 1,500起',
      bestFor: '商務人士、家庭'
    },
    homestay: {
      title: '民宿',
      description: '溫馨舒適，在地特色，體驗當地生活',
      features: ['在地體驗', '溫馨服務', '特色裝潢'],
      price: 'NT$ 800起',
      bestFor: '情侶、小團體'
    },
    hostel: {
      title: '青年旅館',
      description: '經濟實惠，社交氛圍濃厚，適合背包客',
      features: ['共用空間', '社交活動', '經濟實惠'],
      price: 'NT$ 400起',
      bestFor: '背包客、學生'
    },
    villa: {
      title: '別墅',
      description: '私密空間，豪華設施，適合團體和家庭',
      features: ['私人空間', '豪華設施', '專屬服務'],
      price: 'NT$ 3,000起',
      bestFor: '團體、家庭'
    }
  },
  areas: {
    title: '推薦區域',
    list: [
      {
        icon: '🌊',
        name: '七星潭',
        description: '美麗的海岸線，寧靜的環境',
        highlight: '海景第一排'
      },
      {
        icon: '🏔️',
        name: '太魯閣',
        description: '壯麗的山景，清新的空氣',
        highlight: '山景環繞'
      },
      {
        icon: '🏙️',
        name: '花蓮市區',
        description: '便利的生活機能，交通方便',
        highlight: '生活便利'
      }
    ]
  },
  process: {
    title: '預約流程',
    steps: [
      {
        title: '選擇住宿',
        description: '根據需求和預算選擇最適合的住宿類型'
      },
      {
        title: '填寫資訊',
        description: '提供入住時間、人數等詳細資訊'
      },
      {
        title: '確認預約',
        description: '我們會立即確認您的預約並發送確認信'
      },
      {
        title: '享受住宿',
        description: '舒適入住，享受完美的住宿體驗'
      }
    ]
  }
};

// 英文翻譯
const enUS = {
  title: 'Accommodation Recommendations',
  subtitle: 'From sea view homestays to city hotels, from budget hostels to luxury villas, we carefully select the best accommodation options for digital nomads in Hualien',
  cta: 'Book Accommodation',
  startBooking: 'Start Booking Now',
  features: {
    view: {
      title: 'Amazing Views',
      description: 'Sea view, mountain view, city view to meet different needs'
    },
    nomad: {
      title: 'Nomad Friendly',
      description: 'High-speed WiFi, workspace, long-term stay discounts'
    },
    price: {
      title: 'Affordable Prices',
      description: 'From budget to luxury, meeting various budgets'
    },
    community: {
      title: 'Community Atmosphere',
      description: 'Connect with other digital nomads and build networks'
    }
  },
  types: {
    title: 'Accommodation Types',
    hotel: {
      title: 'Hotel',
      description: 'Professional service, complete facilities, suitable for business and leisure',
      features: ['24hr Front Desk', 'Gym', 'Restaurant'],
      price: 'NT$ 1,500+',
      bestFor: 'Business people, families'
    },
    homestay: {
      title: 'Homestay',
      description: 'Cozy and comfortable, local characteristics, experience local life',
      features: ['Local Experience', 'Warm Service', 'Unique Decor'],
      price: 'NT$ 800+',
      bestFor: 'Couples, small groups'
    },
    hostel: {
      title: 'Hostel',
      description: 'Economical, strong social atmosphere, suitable for backpackers',
      features: ['Common Space', 'Social Activities', 'Economical'],
      price: 'NT$ 400+',
      bestFor: 'Backpackers, students'
    },
    villa: {
      title: 'Villa',
      description: 'Private space, luxury facilities, suitable for groups and families',
      features: ['Private Space', 'Luxury Facilities', 'Exclusive Service'],
      price: 'NT$ 3,000+',
      bestFor: 'Groups, families'
    }
  },
  areas: {
    title: 'Recommended Areas',
    list: [
      {
        icon: '🌊',
        name: 'Qixingtan',
        description: 'Beautiful coastline, peaceful environment',
        highlight: 'First row sea view'
      },
      {
        icon: '🏔️',
        name: 'Taroko',
        description: 'Magnificent mountain views, fresh air',
        highlight: 'Surrounded by mountains'
      },
      {
        icon: '🏙️',
        name: 'Hualien City',
        description: 'Convenient amenities, easy transportation',
        highlight: 'Convenient living'
      }
    ]
  },
  process: {
    title: 'Booking Process',
    steps: [
      {
        title: 'Choose Accommodation',
        description: 'Select the most suitable accommodation type based on needs and budget'
      },
      {
        title: 'Fill Information',
        description: 'Provide check-in time, number of guests and other details'
      },
      {
        title: 'Confirm Booking',
        description: 'We will immediately confirm your booking and send confirmation'
      },
      {
        title: 'Enjoy Stay',
        description: 'Comfortable check-in and enjoy perfect accommodation experience'
      }
    ]
  }
};

export default StayIntroPage; 